import type { Workbook } from 'exceljs';

import { type ExcelSheetMapping, type ExcelTableMapping } from '../types/excel-mapping';

export type PopulateWorkbookResult = {
  populated: number;
  skipped: string[];
};

/**
 * Populates an ExcelJS workbook using the given sheet mapping and source data.
 *
 * Resolves each mapping entry's range name in the workbook's defined names
 * and writes the corresponding value into the target cell.
 *
 * @returns Summary of how many cells were populated and which range names were not found.
 */
export function populateWorkbook<TData>({
  workbook,
  mapping,
  data,
}: {
  workbook: Workbook;
  mapping: ExcelSheetMapping<TData>;
  data: TData;
}): PopulateWorkbookResult {
  let populated = 0;
  const skipped: string[] = [];

  for (const field of mapping.fields) {
    const value = field.getValue(data);

    // skip fields that resolve to nothing
    if (value === undefined || value === null) {
      continue;
    }

    const { ranges } = workbook.definedNames.getRanges(field.rangeName);

    if (!ranges || ranges.length === 0) {
      skipped.push(field.rangeName);
      continue;
    }

    const range = ranges[0];

    if (!range) {
      skipped.push(field.rangeName);
      continue;
    }

    // range format: "'Sheet Name'!$E$52" or "Sheet1!$A$1"
    const match = range.match(/^(?:'([^']+)'|([^!]+))!\$?([A-Z]+)\$?(\d+)$/);

    if (!match) {
      skipped.push(field.rangeName);
      continue;
    }

    const sheetName = match[1] ?? match[2];
    const col = match[3];
    const row = parseInt(match[4], 10);

    const worksheet = workbook.getWorksheet(sheetName);

    if (!worksheet) {
      skipped.push(field.rangeName);
      continue;
    }

    const cell = worksheet.getCell(`${col}${row}`);
    cell.value = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value;
    populated++;
  }

  return { populated, skipped };
}

/**
 * Resolves a named range to its sheet name and row number.
 * Expects a range like "'Sheet Name'!$A$29" or "Sheet1!$A$1".
 */
function resolveStartRange(
  workbook: Workbook,
  rangeName: string,
): { sheetName: string; startRow: number } | undefined {
  const { ranges } = workbook.definedNames.getRanges(rangeName);
  const range = ranges?.[0];
  if (!range) return undefined;

  const match = range.match(/^(?:'([^']+)'|([^!]+))!\$?([A-Z]+)\$?(\d+)$/);
  if (!match) return undefined;

  return {
    sheetName: match[1] ?? match[2],
    startRow: parseInt(match[4], 10),
  };
}

/**
 * Populates a tabular Excel sheet by writing each entry as a row.
 *
 * The starting row and sheet are resolved from the mapping's `startRangeName`
 * defined in the Excel template's Name Manager.
 *
 * @returns Summary of how many cells were populated.
 */
export function populateTable<TEntry>({
  workbook,
  mapping,
  entries,
}: {
  workbook: Workbook;
  mapping: ExcelTableMapping<TEntry>;
  entries: ReadonlyArray<TEntry>;
}): PopulateWorkbookResult {
  let populated = 0;
  const skipped: string[] = [];

  const resolved = resolveStartRange(workbook, mapping.startRangeName);

  if (!resolved) {
    skipped.push(`Range "${mapping.startRangeName}" not found`);
    return { populated, skipped };
  }

  const { sheetName, startRow } = resolved;
  const worksheet = workbook.getWorksheet(sheetName);

  if (!worksheet) {
    skipped.push(`Sheet "${sheetName}" not found`);
    return { populated, skipped };
  }

  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    const row = startRow + i;

    for (const col of mapping.columns) {
      const value = col.getValue(entry, i);

      if (value === undefined || value === null) {
        continue;
      }

      const cell = worksheet.getCell(`${col.column}${row}`);
      cell.value = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value;
      populated++;
    }
  }

  return { populated, skipped };
}
